import { supabase, supabaseUrl } from '@/lib/supabase';

export type HandoffResult =
  | { status: 'ok' }
  | { status: 'invalid_or_expired' }
  | { status: 'network_error' };

/**
 * Extrait `roomId` (chemin `/join/:roomId`) et `token` (fragment `#token=...`)
 * de l'URL courante. Le token vit dans le FRAGMENT (jamais en query string)
 * pour ne jamais apparaître dans un log d'accès HTTP — cf.
 * supabase/functions/visio-handoff-create/index.ts.
 */
export function parseJoinUrl(
  pathname: string,
  hash: string,
): { roomId: string | null; token: string | null } {
  const match = pathname.match(/\/join\/([^/]+)/);
  const roomId = match ? match[1]! : null;
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token');
  return { roomId, token };
}

interface RedeemResponse {
  access_token?: string;
  refresh_token?: string;
  reservation_id?: string;
  error?: string;
}

/**
 * Échange le jeton de handoff contre une session Supabase (édge fn publique
 * `visio-handoff-redeem`), l'établit sur le client de CETTE app, puis nettoie
 * le fragment de l'URL (le jeton est à usage unique — inutile de le garder
 * visible, et un rechargement de page ne doit pas tenter de le rejouer).
 */
export async function redeemHandoff(token: string): Promise<HandoffResult> {
  let body: RedeemResponse;
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/visio-handoff-redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    body = (await res.json()) as RedeemResponse;
    if (!res.ok || !body.access_token || !body.refresh_token) {
      return { status: 'invalid_or_expired' };
    }
  } catch {
    return { status: 'network_error' };
  }

  const { error } = await supabase.auth.setSession({
    access_token: body.access_token,
    refresh_token: body.refresh_token,
  });
  if (error) return { status: 'invalid_or_expired' };

  window.history.replaceState(null, '', window.location.pathname);

  return { status: 'ok' };
}
